import React, { useEffect, useState } from 'react';
import {
  getTenantInfo,
  updateTenantInfo,
  triggerWeatherBackfill,
  captureCurrentWeather
} from '../services/tenantService';
import { Field, FormSection, Notice } from './patterns';
import type { NoticeTone } from './patterns';

const DEFAULTS = {
  temperature: { coldMax: 10, optimalMax: 28 },
  wind:        { calmMax: 2, breezeMax: 15 },
  precipitation: { noneMax: 0, lightMax: 2 }
};

type Numeric = number | '';

const toNumeric = (v: any, fallback: number): Numeric =>
  (v === null || v === undefined || Number.isNaN(Number(v))) ? fallback : Number(v);

const WeatherSettings: React.FC = () => {
  const [coldMax, setColdMax] = useState<Numeric>(DEFAULTS.temperature.coldMax);
  const [optimalMax, setOptimalMax] = useState<Numeric>(DEFAULTS.temperature.optimalMax);
  const [calmMax, setCalmMax] = useState<Numeric>(DEFAULTS.wind.calmMax);
  const [breezeMax, setBreezeMax] = useState<Numeric>(DEFAULTS.wind.breezeMax);
  const [noneMax, setNoneMax] = useState<Numeric>(DEFAULTS.precipitation.noneMax);
  const [lightMax, setLightMax] = useState<Numeric>(DEFAULTS.precipitation.lightMax);

  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillMessage, setBackfillMessage] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [captureMessage, setCaptureMessage] = useState<string | null>(null);

  useEffect(() => {
    getTenantInfo()
      .then(tenant => {
        const t = tenant?.weatherThresholds || {};
        setColdMax(toNumeric(t.temperature?.coldMax, DEFAULTS.temperature.coldMax));
        setOptimalMax(toNumeric(t.temperature?.optimalMax, DEFAULTS.temperature.optimalMax));
        setCalmMax(toNumeric(t.wind?.calmMax, DEFAULTS.wind.calmMax));
        setBreezeMax(toNumeric(t.wind?.breezeMax, DEFAULTS.wind.breezeMax));
        setNoneMax(toNumeric(t.precipitation?.noneMax, DEFAULTS.precipitation.noneMax));
        setLightMax(toNumeric(t.precipitation?.lightMax, DEFAULTS.precipitation.lightMax));
      })
      .catch(err => {
        console.error('Failed to load tenant weather thresholds:', err);
      });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage(null);
    try {
      await updateTenantInfo({
        weatherThresholds: {
          temperature:   { coldMax: Number(coldMax),    optimalMax: Number(optimalMax) },
          wind:          { calmMax: Number(calmMax),    breezeMax:  Number(breezeMax) },
          precipitation: { noneMax: Number(noneMax),    lightMax:   Number(lightMax) }
        }
      });
      setSaveMessage('Thresholds saved successfully.');
    } catch (err) {
      setSaveMessage('Failed to save thresholds. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleBackfill = async () => {
    setBackfilling(true);
    setBackfillMessage(null);
    try {
      const result = await triggerWeatherBackfill();
      setBackfillMessage(result?.message || 'Weather backfill initiated.');
    } catch (err) {
      setBackfillMessage('Failed to start backfill. Please try again.');
    } finally {
      setBackfilling(false);
    }
  };

  const handleCaptureNow = async () => {
    setCapturing(true);
    setCaptureMessage(null);
    try {
      const result = await captureCurrentWeather();
      const t = result?.entry?.temp;
      const w = result?.entry?.windSpeed;
      const summary = (typeof t === 'number' || typeof w === 'number')
        ? ` (temp ${t ?? '—'} °C, wind ${w ?? '—'} km/h)`
        : '';
      setCaptureMessage(`${result?.message || 'Current weather captured'}${summary}.`);
    } catch (err) {
      setCaptureMessage('Failed to capture current weather. Please try again.');
    } finally {
      setCapturing(false);
    }
  };

  const numericInput = (
    id: string,
    label: string,
    value: Numeric,
    onChange: (v: Numeric) => void,
    step = '0.1'
  ) => (
    <Field label={label} controlId={id}>
      <input
        id={id}
        type="number"
        step={step}
        value={value === '' ? '' : value}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') return onChange('');
          const parsed = Number(raw);
          onChange(Number.isNaN(parsed) ? '' : parsed);
        }}
      />
    </Field>
  );

  const alertTone = (message: string): NoticeTone =>
    message.toLowerCase().startsWith('failed') ? 'danger' : 'success';

  return (
    <div className="settings-general">
      <FormSection
        id="weather-thresholds"
        title="Weather Thresholds"
        description={
          'Boundaries that classify each weather reading into Cold / Optimal / Hot, ' +
          'Calm / Breeze / Strong, and None / Light / Heavy buckets. Each value is ' +
          'the upper bound (inclusive) of the lower bucket.'
        }
      >
        <h4 className="settings-subheading">Temperature (°C)</h4>
        <div className="settings-numeric-grid">
          {numericInput('coldMax', 'Cold maximum', coldMax, setColdMax)}
          {numericInput('optimalMax', 'Optimal maximum', optimalMax, setOptimalMax)}
        </div>

        <h4 className="settings-subheading">Wind (km/h)</h4>
        <div className="settings-numeric-grid">
          {numericInput('calmMax', 'Calm maximum', calmMax, setCalmMax)}
          {numericInput('breezeMax', 'Breeze maximum', breezeMax, setBreezeMax)}
        </div>

        <h4 className="settings-subheading">Precipitation (mm/hr)</h4>
        <div className="settings-numeric-grid">
          {numericInput('noneMax', 'No precipitation maximum', noneMax, setNoneMax)}
          {numericInput('lightMax', 'Light precipitation maximum', lightMax, setLightMax)}
        </div>

        {saveMessage && (
          <Notice tone={alertTone(saveMessage)}>{saveMessage}</Notice>
        )}
        <div className="settings-section-actions">
          <button
            type="button"
            className="pat-btn pat-btn--primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save Thresholds'}
          </button>
        </div>
      </FormSection>

      <FormSection
        id="weather-backfill"
        title="Manual Weather Backfill"
        description={
          'Re-fetches the most recent 48 hours of weather history from WeatherAPI ' +
          "for this farm's central location. Use this if recent weather appears " +
          'missing or to repopulate after a connectivity issue. Existing entries ' +
          'are not duplicated.'
        }
      >
        {backfillMessage && (
          <Notice tone={alertTone(backfillMessage)}>{backfillMessage}</Notice>
        )}
        <div className="settings-section-actions">
          <button
            type="button"
            className="pat-btn pat-btn--outline"
            onClick={handleBackfill}
            disabled={backfilling}
          >
            {backfilling ? 'Starting…' : 'Run Manual Backfill'}
          </button>
        </div>
      </FormSection>

      <FormSection
        id="weather-capture"
        title="Capture Current Weather"
        description={
          "Records a single weather reading for right now. WeatherAPI's history " +
          'endpoint typically lags several hours behind real time, so this on-demand ' +
          'capture closes the gap between the most recent backfill and the next ' +
          'hourly cron without waiting.'
        }
      >
        {captureMessage && (
          <Notice tone={alertTone(captureMessage)}>{captureMessage}</Notice>
        )}
        <div className="settings-section-actions">
          <button
            type="button"
            className="pat-btn pat-btn--outline"
            onClick={handleCaptureNow}
            disabled={capturing}
          >
            {capturing ? 'Capturing…' : 'Capture Now'}
          </button>
        </div>
      </FormSection>
    </div>
  );
};

export default WeatherSettings;
